import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Paper,
    Alert,
    Stack,
    Chip,
    Card,
    CardContent,
    CircularProgress,
    useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PersonIcon from '@mui/icons-material/Person';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { useAppSelector } from '../../hooks/redux.hooks';
import axiosInstance from '../../services/axiosInstance';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import AccountSelector from '../common/AccountSelector';
import { chartOfAccountsService } from '../../services/chartOfAccounts.service';
import CostCenterSelector from '../common/CostCenterSelector';
import {
    CreateVoucherDto,
    CreateVoucherDetailDto,
    VoucherDto,
    UpdateVoucherDto,
} from '../../types/vouchers.types';
import { VOUCHER_SIDE } from '../../constants/vouchers.constants';
import { cashPaymentsService } from '../../services/cashPayments.service';
import apiClient from '../../services/api';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';
import {
    interpretVoiceCommand,
    parseSpokenDate,
    parseSpokenAmount,
    normalizeVoucherRef,
} from './v2/voiceCommands';

// ─────────────────────────────────────────────────────────────
// V2 EXPERIMENTAL DIALOG — sandbox copy. Original CashPaymentDialog.tsx is untouched.
// Reached only via the dedicated /smart-entry-v2 page.
// Layers a keyword-detection + 5-alternatives dropdown on top of the
// production Smart Entry parser (same /api/SmartEntry/parse endpoint).
// ─────────────────────────────────────────────────────────────

interface CashPaymentDialogV2Props {
    open: boolean;
    onClose: () => void;
    onSave: (dto: CreateVoucherDto | UpdateVoucherDto) => Promise<void>;
    editVoucher?: VoucherDto | null;
    cashParentAccountId: number;
}

interface DebitLine {
    id: string;
    accountId: number | null;
    costCenterId: number | null;
    amount: number;
    description: string;
    /** Up to 5 ranked alternatives returned by Smart Entry — clickable by user. */
    accountAlternatives?: Array<{ accountId: number; accountCode: string; accountName: string; accountNameAr: string; score: number }>;
}

const CashPaymentDialogV2: React.FC<CashPaymentDialogV2Props> = ({
    open,
    onClose,
    onSave,
    editVoucher,
    cashParentAccountId,
}) => {
    const { language } = useLanguage();
    const isRTL = language === 'ar';
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const { minDate, maxDate } = useFiscalDateRange();

    const gridRef = useRef<HTMLDivElement>(null);
    useGridEnterNavigation(gridRef);

    const [voucherNumber, setVoucherNumber] = useState<string>('');
    const [voucherDate, setVoucherDate] = useState<string>(
        new Date().toISOString().split('T')[0]
    );
    const [notes, setNotes] = useState<string>('');
    const [payeeName, setPayeeName] = useState<string>('');
    const [creditAccountId, setCreditAccountId] = useState<number | null>(null);
    const [creditAmount, setCreditAmount] = useState<number>(0);
    const [debitLines, setDebitLines] = useState<DebitLine[]>([
        {
            id: Math.random().toString(),
            accountId: null,
            costCenterId: null,
            amount: 0,
            description: '',
        },
    ]);
    const [loading, setLoading] = useState(false);
    const [checkingNumber, setCheckingNumber] = useState(false);
    const [error, setError] = useState<string>('');

    // ── Smart Entry (Super Admin only) ────────────────────────
    const isSuperAdmin = useAppSelector(s => s.auth.user?.isSuperAdmin ?? false);
    const [smartOpen, setSmartOpen] = useState(false);
    const [smartText, setSmartText] = useState('');
    const [smartLoading, setSmartLoading] = useState(false);

    // ── V2 additions ──────────────────────────────────────────
    // Active line index for field-targeted edits ("سطر 2 …" or fallback when no
    // line number is spoken). 0-based, points into debitLines.
    const [activeLineIndex, setActiveLineIndex] = useState<number>(0);
    // Per-line DOM refs so we can scroll the active row into view automatically
    // when the user selects it (voice command, chip click, or "add new line").
    const lineRefs = useRef<Array<HTMLDivElement | null>>([]);
    // Per-line raw amount string. Lets the user TYPE "56.500" and keep the
    // trailing zeros visible — a plain numeric value would render as "56.5"
    // because JS Number drops them. Keyed by DebitLine.id so add/remove sync.
    const [amountRaw, setAmountRaw] = useState<Record<string, string>>({});
    const displayAmount = (line: DebitLine): string => {
        if (amountRaw[line.id] != null) return amountRaw[line.id];
        return line.amount > 0 ? line.amount.toFixed(3) : '';
    };
    // Per-line account alternatives (top-5 returned by /SmartEntry/parse).
    // Keyed by DebitLine.id so adds/removes don't desync.
    const [lineAlternatives, setLineAlternatives] = useState<Record<string,
        Array<{ accountId: number; accountCode: string; accountName: string; accountNameAr: string; score: number }>>>({});
    // Toast helper that respects RTL automatically.
    const v2Toast = (msg: string, kind: 'success' | 'info' | 'warning' = 'success') => {
        if (kind === 'success') toast.success(msg);
        else if (kind === 'warning') toast.warning(msg);
        else toast.info(msg);
    };

    // V2: voice-input MODE. When set to a value, the user's spoken text is
    // routed directly into that field — they don't have to say the keyword.
    // null = "auto" mode: keywords inside the spoken text decide the destination.
    type ActiveMode = null | 'date' | 'voucher' | 'payee' | 'notes' | 'amount' | 'account';
    const [activeMode, setActiveMode] = useState<ActiveMode>(null);
    // Field-level flash highlight: when a voice command writes to a field, we
    // briefly glow that field so the user can SEE where the value landed.
    type FlashKey = 'date' | 'voucher' | 'payee' | 'notes' | `line-${number}` | null;
    const [flashField, setFlashField] = useState<FlashKey>(null);
    const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const flash = (key: Exclude<FlashKey, null>) => {
        setFlashField(key);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setFlashField(null), 1800);
    };

    // Auto-scroll the active debit row into view, accounting for the sticky
    // voice banner that covers the top of the scroll container.
    const scrollActiveLineIntoView = () => {
        const el = lineRefs.current[activeLineIndex];
        if (!el) return;
        setTimeout(() => {
            let container: HTMLElement | null = el.parentElement;
            while (container) {
                const ov = window.getComputedStyle(container).overflowY;
                if (ov === 'auto' || ov === 'scroll') break;
                container = container.parentElement;
            }
            if (!container) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }
            const cRect = container.getBoundingClientRect();
            const eRect = el.getBoundingClientRect();
            let stickyOffset = 220;
            const sticky = container.querySelector<HTMLElement>(':scope > div > .MuiPaper-root[class*="MuiPaper"]');
            if (sticky && window.getComputedStyle(sticky).position === 'sticky') {
                stickyOffset = sticky.getBoundingClientRect().height + 12;
            }
            const targetTop = container.scrollTop + (eRect.top - cRect.top) - stickyOffset;
            container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
        }, 80);
    };
    useEffect(() => {
        scrollActiveLineIntoView();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeLineIndex, debitLines.length]);
    // Also scroll when the user switches into a per-line mode (account/amount/desc)
    // so the row they're about to edit is visible without manual scroll.
    useEffect(() => {
        if (activeMode === 'account' || activeMode === 'amount') {
            scrollActiveLineIntoView();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeMode]);
    const flashSx = (key: Exclude<FlashKey, null>) => flashField === key ? ({
        '& .MuiOutlinedInput-root, & .MuiInputBase-root': {
            transition: 'box-shadow 0.2s, background-color 0.2s',
            boxShadow: '0 0 0 3px rgba(251,191,36,0.55)',
            backgroundColor: 'rgba(251,191,36,0.08)',
        },
    }) : {};
    const activeModeLabel = (m: ActiveMode): string => {
        if (m === 'date')    return isRTL ? 'التاريخ'    : 'date';
        if (m === 'voucher') return isRTL ? 'رقم السند' : 'voucher';
        if (m === 'payee')   return isRTL ? 'المستفيد'  : 'payee';
        if (m === 'notes')   return isRTL ? 'الملاحظات' : 'notes';
        if (m === 'amount')  return isRTL ? 'المبلغ'    : 'amount';
        if (m === 'account') return isRTL ? 'الحساب'    : 'account';
        return '';
    };

    // ── Voice Smart Entry (Web Speech API — Chrome/Edge built-in) ──
    const [voiceListening, setVoiceListening] = useState(false);
    const [voiceInterim, setVoiceInterim] = useState('');
    const [voiceLang, setVoiceLang] = useState<'ar-SA' | 'en-US'>('ar-SA');
    const [audioLevel, setAudioLevel] = useState(0); // 0-100, real-time mic input level
    const voiceRecognitionRef = useRef<any>(null);
    const voiceShouldRestartRef = useRef(false);
    const voiceLangRef = useRef<'ar-SA' | 'en-US'>('ar-SA');
    const voiceRawRef = useRef('');  // raw accumulated voice transcript (pre-transform)
    const audioCtxRef = useRef<AudioContext | null>(null);
    const audioStreamRef = useRef<MediaStream | null>(null);
    const audioAnalyserRef = useRef<AnalyserNode | null>(null);
    const audioRafRef = useRef<number | null>(null);

    // ═════════════════════════════════════════════════════════════
    // Voice Transcript → Smart Entry Template Transformer
    // Converts spoken commands into the structured template format.
    // Trigger vocabulary is intentionally WIDE for tolerance.
    // ═════════════════════════════════════════════════════════════
    const transformVoiceText = (raw: string): string => {
        if (!raw) return '';
        let text = raw;

        // ── Step 1: Hindi/Persian/Indic digits → ASCII ────────
        text = text.replace(/[٠-٩]/g, (c: string) =>
            String.fromCharCode(c.charCodeAt(0) - 0x0660 + 0x30));
        text = text.replace(/[۰-۹]/g, (c: string) =>
            String.fromCharCode(c.charCodeAt(0) - 0x06F0 + 0x30));

        // ── Step 1.5: Strip Arabic diacritics (tashkeel) ────────
        // Chrome STT sometimes adds fatha/damma/kasra marks. Triggers must
        // match the bare letters, so strip these before further processing.
        text = text.replace(/[ً-ْٰٓ-ٕ]/g, '');

        // ── Step 1.7: Normalize numeric punctuation ─────────────
        // Chrome may insert a literal "." or "," between digits, possibly
        // with surrounding spaces, OR split decimal digits ("4 4 5" → "445").
        // Collapse them all into a unified "X.YYY" form.
        text = text.replace(/(\d+)\s*[.,،]\s*((?:\d\s*){1,5})(?=\s|$|[^\d\s.,])/g,
            (_m: string, a: string, b: string) => a + '.' + b.replace(/\s+/g, ''));

        // ── Step 2: Normalize whitespace ───────────────────────
        text = text.replace(/[ \t ]+/g, ' ').trim();

        // ── Step 3: NEW LINE triggers → \n-\n ─────────────────
        // Expanded vocabulary (Arabic, English, transliterations, common variants)
        const newLineTriggers = [
            'سطر\\s*جديد', 'اسطر\\s*جديد', 'السطر\\s*التالي', 'سطر\\s*تالي',
            'خط\\s*جديد', 'شرطة\\s*جديدة', 'فاصل', 'فاصل\\s*جديد',
            'نيو\\s*لاين', 'نيولاين',
            'تالي', 'التالي', 'تال', 'نكست',
            'new\\s*line', 'newline',
            'next', 'next\\s*line',
            'enter', 'انتر',
            'break', 'بريك',
            'separator', 'سيباريتر',
            'تفصيلة\\s*جديدة', 'سطر\\s*ثاني', 'سطر\\s*أخر', 'سطر\\s*اخر',
        ];
        const newLineRx = new RegExp('\\s*(?:' + newLineTriggers.join('|') + ')\\s*[،.,]?\\s*', 'giu');
        text = text.replace(newLineRx, '\n-\n');

        // ── Step 4: BENEFICIARY prefix normalization ───────────
        const payeeTriggers = [
            'المستفيد', 'مستفيد', 'إلى', 'الى\\s+(?=\\D)', // "الى X" but not "الى 5"
            'بنفيشاري', 'بينافيشري', 'بنيفيشري', 'بنيفيشاري',
            'beneficiary', 'paid\\s*to', 'paidto', 'payable\\s*to', 'payee',
            'pay\\s*to', 'pay',
            'المدفوع\\s*له', 'المدفوع\\s*ل', 'لصالح',
            'لمن', 'لـ', 'باسم',
        ];
        const payeeRx = new RegExp(
            '(^|\\n)\\s*(?:' + payeeTriggers.join('|') + ')\\s*[:.،]?\\s*',
            'giu');
        text = text.replace(payeeRx, '$1المستفيد ');

        // ── Step 5: Decimal point ("نقطة" / "dot") between digits ──
        // Capture ALL following digits (possibly separated by spaces) into the
        // decimal portion. E.g. "493 dot 4 4 5" → "493.445"
        const dotTriggers = ['نقطه', 'نقطة', 'فاصله', 'فاصلة', 'فصلة',
            'دوت', 'بوينت', 'dot', 'point', 'period', 'decimal'];
        const dotRx = new RegExp(
            '(\\d+)\\s*(?:' + dotTriggers.join('|') + ')\\s*((?:\\d\\s*){1,6})',
            'giu'
        );
        text = text.replace(dotRx, (_m: string, intPart: string, decPart: string) =>
            intPart + '.' + decPart.replace(/\s+/g, ''));

        // ── Step 6: KD-Fils combined patterns (Kuwait amount format) ──
        // 6a: "X دينار [dot|.|و] Y فلس" → "X.YYYkd" (full Kuwait amount form)
        text = text.replace(
            /(\d+)\s*(?:دينار(?:\s*كويتي)?|دنانير|دك|د\.\s*ك|kd|kwd|kuwaiti\s*dinar|كي\s*دي|كويتي)\s*(?:\.|نقطه|نقطة|دوت|dot|point|و|and|\+)?\s*(\d{1,3})\s*(?:فلس|فلوس|fils|فلسا|فلساً)\b/gi,
            (_m: string, kd: string, fils: string) => kd + '.' + fils.padStart(3, '0') + 'kd'
        );
        // 6b: "X.Y فلس" → "X.YYYkd"
        text = text.replace(
            /(\d+)\.(\d{1,3})\s*(?:فلس|فلوس|fils|فلسا|فلساً)\b/gi,
            (_m: string, kd: string, fils: string) => kd + '.' + fils.padStart(3, '0') + 'kd'
        );
        // 6c: "X و Y فلس" → "X.YYYkd"
        text = text.replace(
            /(\d+)\s+(?:و|and|\+)\s+(\d{1,3})\s*(?:فلس|فلوس|fils|فلسا|فلساً)\b/gi,
            (_m: string, kd: string, fils: string) => kd + '.' + fils.padStart(3, '0') + 'kd'
        );

        // ── Step 6.5: AMOUNT trigger → "Xkd" ────────────────────
        // Triggers: المبلغ / amount / etc. After the trigger, capture the number
        // (with optional decimal) and any trailing kd-suffix variant. Output: "Xkd".
        const amountTriggers = [
            'المبلغ', 'مبلغ', 'بمبلغ', 'بالمبلغ',
            'بقيمة', 'قيمة', 'بسعر', 'سعر', 'ثمن',
            'amount', 'the\\s*amount', 'amounts',
            'value', 'price', 'cost', 'total',
        ];
        // Comprehensive KD suffix variants (Arabic + English + transliterations)
        const kdSuffixes = [
            'دينار(?:\\s*كويتي)?', 'دنانير',
            'دك', 'د\\s*\\.?\\s*ك', 'د\\.?\\s*ك\\.?',
            'كي\\s*دي', 'ك\\s*دي', 'كي\\s*\\.?\\s*دي', 'ك\\s*\\.?\\s*د',
            'دي\\s*كي', 'دي\\s*ك', 'كويتي',
            'kd', 'kwd', 'k\\s*\\.?\\s*d', 'k\\.?d',
            'kuwaiti\\s*dinar',
        ];
        text = text.replace(
            new RegExp(
                '(?<=^|[\\s،,.])(?:' + amountTriggers.join('|') +
                ')[\\s:،,.]+(\\d+(?:\\.\\d+)?)\\s*(?:' + kdSuffixes.join('|') + ')?(?:(?=\\s)|(?=$)|(?=[،,.]))',
                'giu'
            ),
            (_m: string, num: string) => num + 'kd'
        );

        // ── Step 7: VOUCHER trigger + collect digits ──────────
        // Use lookbehind (?<=^|[\s،,.]) instead of \b because JavaScript \b
        // doesn't recognize Arabic word boundaries.
        const voucherTriggers = [
            'في', 'voucher', 'voucher\\s*number', 'voucher\\s*no', 'voucher\\s*#',
            'الفاوتشر', 'الفوتشر', 'فاوتشر', 'فوتشر',
            'باوتشر', 'بافتشر', 'بوتشر', 'باؤتشر', 'باؤوتشر', 'باؤچر',
            'ڤاوتشر', 'ڤوتشر', 'ڤافتشر',
            'سند\\s*رقم', 'رقم\\s*السند', 'رقم\\s*الفاوتشر', 'رقم\\s*الفوتشر', 'رقم\\s*الباوتشر',
            'نمبر\\s*السند', 'السند\\s*نمبر', 'نمبر\\s*فاوتشر', 'نمبر\\s*باوتشر',
            'v\\.?', 'vee', 'في\\s*رقم', 'الفي',
        ];
        const voucherRx = new RegExp(
            '(?<=^|[\\s،,.])(?:' + voucherTriggers.join('|') + ')\\s+([\\d\\s]+?)(?=$|\\n|[^\\d\\s])',
            'giu');
        text = text.replace(voucherRx, (_m: string, digits: string) =>
            'V ' + digits.replace(/\s+/g, ''));

        // ── Step 8: DATE trigger + collect numbers (→ dashes) ─
        // First: handle "today" → fill date with today
        const todayPattern = new RegExp(
            '(?<=^|[\\s،,.])(?:(?:التاريخ|تاريخ|date|بتاريخ)\\s+)?(?:اليوم|today|todays\\s*date|today\'?s)(?=$|[\\s،,.])',
            'giu');
        text = text.replace(todayPattern, () => {
            const t = new Date();
            return 'التاريخ ' + t.getDate() + '-' + (t.getMonth() + 1) + '-' + t.getFullYear();
        });

        const dateTriggers = [
            'التاريخ', 'تاريخ', 'بتاريخ', 'بتارخ', 'التارخ', 'تأريخ', 'date',
            'ديت', 'the\\s*date',
        ];
        const dateRx = new RegExp(
            '(?<=^|[\\s،,.])(?:' + dateTriggers.join('|') + ')\\s+([\\d\\s\\/\\-\\.\\\\]+?)(?=$|\\n|[^\\d\\s\\/\\-\\.\\\\])',
            'giu');
        text = text.replace(dateRx, (_m: string, nums: string) => {
            const parts = nums.trim().split(/[\s\/\-\.\\]+/).filter((p: string) => p.length > 0);
            return parts.length >= 1 ? 'التاريخ ' + parts.join('-') : _m;
        });

        // ── Step 9: Generic KD suffix without trigger → "Xkd" ───
        // Catches "475 دينار" or "493.445 kd" without the المبلغ trigger.
        text = text.replace(
            /(\d+(?:\.\d+)?)\s*(?:دينار(?:\s*كويتي)?|دنانير|دك|د\s*\.?\s*ك|كي\s*دي|ك\s*دي|كي\s*\.?\s*دي|دي\s*كي|كويتي|kuwaiti\s*dinar|kd|kwd|k\s*\.?\s*d)(?=$|[\s،,.]|[^a-zA-Z؀-ۿ])/gi,
            (_m: string, num: string) => num + 'kd');

        // ── Step 10: Standalone numbers on a line → append "kd" ──
        text = text.split('\n').map((line: string) => {
            const t = line.trim();
            // Skip V/Date lines
            if (/^\s*V\b/i.test(t)) return line;
            if (/^\s*(?:التاريخ|date)\b/i.test(t)) return line;
            // Skip the dash separator
            if (t === '-') return line;
            // Pure number line (with optional decimal) → append kd
            if (/^\d+(?:\.\d+)?$/.test(t)) return t + 'kd';
            return line;
        }).join('\n');

        // ── Step 11: Cleanup ────────────────────────────────────
        // Collapse spaces
        text = text.replace(/[ \t]+/g, ' ');
        // Strip spaces around newlines
        text = text.replace(/ *\n */g, '\n');
        // Collapse 3+ newlines to 2 (keeps -\n-\n separators tight)
        text = text.replace(/\n{3,}/g, '\n\n');
        // Remove stray "التاريخ التاريخ" (defensive)
        text = text.replace(/التاريخ\s+التاريخ/g, 'التاريخ');
        // Remove stray "المستفيد المستفيد"
        text = text.replace(/المستفيد\s+المستفيد/g, 'المستفيد');
        // Remove duplicate "V V"
        text = text.replace(/\bV\s+V\b/g, 'V');
        // Defensive: avoid double "kdkd"
        text = text.replace(/kd\s*kd\b/gi, 'kd');
        // Defensive: trim "kd" appearing alone on a line (no number) — orphan from cleanup
        text = text.split('\n').map(l => /^\s*kd\s*$/i.test(l) ? '' : l).filter(l => l !== '').join('\n');
        // Final newline pass
        text = text.replace(/\n{3,}/g, '\n\n');

        return text.trim();
    };

    // Start a separate audio analyser so the user SEES if the mic is being picked up.
    // Independent from SpeechRecognition (which doesn't expose level data).
    const startAudioLevelMeter = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            audioStreamRef.current = stream;
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            audioCtxRef.current = ctx;
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 512;
            source.connect(analyser);
            audioAnalyserRef.current = analyser;
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            const tick = () => {
                analyser.getByteTimeDomainData(dataArray);
                // RMS-style level computation
                let sum = 0;
                for (let i = 0; i < dataArray.length; i++) {
                    const v = (dataArray[i] - 128) / 128;
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / dataArray.length);
                setAudioLevel(Math.min(100, Math.round(rms * 300)));
                audioRafRef.current = requestAnimationFrame(tick);
            };
            tick();
        } catch (err) {
            // Permission denied or no mic — show clear error
            toast.error(isRTL
                ? 'لم نتمكن من الوصول للميكروفون. تحقق من إعدادات Windows والمتصفح.'
                : 'Cannot access microphone. Check Windows and browser settings.');
        }
    };

    const stopAudioLevelMeter = () => {
        if (audioRafRef.current) cancelAnimationFrame(audioRafRef.current);
        audioRafRef.current = null;
        try { audioStreamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
        try { audioCtxRef.current?.close(); } catch {}
        audioStreamRef.current = null;
        audioCtxRef.current = null;
        audioAnalyserRef.current = null;
        setAudioLevel(0);
    };

    const isSpeechSupported = (): boolean => {
        return !!(window as any).webkitSpeechRecognition || !!(window as any).SpeechRecognition;
    };

    // Build a fresh recognition instance and start it.
    // Called both on initial Voice Input click AND on every silent auto-restart.
    const buildAndStartRecognition = () => {
        const SR = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
        if (!SR) return;
        try {
            const rec = new SR();
            rec.continuous = true;
            rec.interimResults = true;
            rec.lang = voiceLangRef.current;
            rec.maxAlternatives = 1;

            rec.onresult = (event: any) => {
                let interim = '';
                let final = '';
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        final += transcript + ' ';
                    } else {
                        interim += transcript;
                    }
                }
                setVoiceInterim(interim);
                if (final.trim()) {
                    // Accumulate raw transcript and re-transform the full text every time.
                    // Re-transforming the full buffer (instead of appending transformed chunks)
                    // preserves cross-chunk context — e.g. "في" arrives in one chunk,
                    // "6 3 2 7" in the next; transform of the joined raw still produces "V 6327".
                    voiceRawRef.current = (voiceRawRef.current + ' ' + final.trim()).trim();
                    setSmartText(transformVoiceText(voiceRawRef.current));
                }
            };

            rec.onerror = (e: any) => {
                const err = e?.error || '';
                // "no-speech" and "aborted" are normal during silent periods — just restart silently.
                // "audio-capture" means no mic detected — also try to restart.
                if (err === 'no-speech' || err === 'aborted' || err === 'audio-capture') {
                    return;  // will be re-attempted in onend if user hasn't stopped
                }
                if (err === 'not-allowed' || err === 'service-not-allowed') {
                    toast.error(isRTL
                        ? 'يجب السماح للموقع باستخدام الميكروفون. افتح إعدادات المتصفح وامنح الإذن.'
                        : 'Microphone permission denied. Please enable mic access in browser settings.');
                    voiceShouldRestartRef.current = false;
                    setVoiceListening(false);
                    setVoiceInterim('');
                    return;
                }
                // Unknown errors — show but keep listening
                toast.warning((isRTL ? 'تحذير: ' : 'Warning: ') + err);
            };

            rec.onend = () => {
                // Browser auto-stops on long silence — restart unless user pressed Stop.
                if (voiceShouldRestartRef.current) {
                    setTimeout(() => {
                        if (voiceShouldRestartRef.current) buildAndStartRecognition();
                    }, 200);
                } else {
                    setVoiceListening(false);
                    setVoiceInterim('');
                }
            };

            voiceRecognitionRef.current = rec;
            rec.start();
        } catch (err: any) {
            // start() can throw "InvalidStateError" if already started — ignore and retry
            setTimeout(() => {
                if (voiceShouldRestartRef.current) buildAndStartRecognition();
            }, 300);
        }
    };

    const startVoiceCapture = async () => {
        if (!isSpeechSupported()) {
            toast.error(isRTL
                ? 'متصفحك لا يدعم الإدخال الصوتي. استخدم Chrome أو Edge.'
                : 'Your browser does not support voice input. Use Chrome or Edge.');
            return;
        }
        voiceLangRef.current = voiceLang;
        voiceShouldRestartRef.current = true;
        // Initialize raw buffer from current text (preserves prior content, allows resume)
        if (!voiceRawRef.current) voiceRawRef.current = smartText;
        setVoiceListening(true);
        await startAudioLevelMeter();
        buildAndStartRecognition();
        toast.info(isRTL
            ? `استمع (${voiceLang === 'ar-SA' ? 'عربي' : 'إنجليزي'})... قل "سطر جديد" للفصل بين الحقول`
            : `Listening (${voiceLang === 'ar-SA' ? 'AR' : 'EN'})... say "new line" between fields`);
    };

    const stopVoiceCapture = () => {
        voiceShouldRestartRef.current = false;
        try { voiceRecognitionRef.current?.stop(); } catch {}
        stopAudioLevelMeter();
        setVoiceListening(false);
        setVoiceInterim('');
    };

    // Cleanup recognition when MAIN dialog closes (not the sub-dialog which we no longer use in V2)
    useEffect(() => {
        if (!open && voiceListening) {
            stopVoiceCapture();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // V2: auto-apply after the user stops dictating for ~1.5s.
    // Triggered whenever smartText changes while voice is listening.
    const autoApplyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (!voiceListening) return;
        if (!smartText.trim()) return;
        if (autoApplyTimerRef.current) clearTimeout(autoApplyTimerRef.current);
        autoApplyTimerRef.current = setTimeout(() => {
            // Only auto-apply if there's still smart text and we're still listening.
            // (Guard against race when user clears or stops mid-debounce.)
            if (smartText.trim() && voiceListening) {
                applySmartParse();
            }
        }, 1500);
        return () => {
            if (autoApplyTimerRef.current) clearTimeout(autoApplyTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [smartText, voiceListening]);

    // ─────────────────────────────────────────────────────────
    // V2 SMART PARSE — keyword-aware
    //
    //  - If the user spoke a field-targeted command ("التاريخ …", "سطر 2 …",
    //    "المستفيد …"), apply ONLY that patch without disturbing the rest.
    //  - Otherwise: full free-dictation parse (same behavior as V1 Smart Entry).
    //
    // Each branch is independent and short — easier to debug per case.
    // ─────────────────────────────────────────────────────────
    /** Clear all voice buffers so the next utterance starts clean.
     *  Called after EVERY successful field application. */
    const clearVoiceBuffers = () => {
        setSmartText('');
        setVoiceInterim('');
        voiceRawRef.current = '';
    };

    /** Build the spoken text we hand to interpretVoiceCommand. When a
     *  field-mode button is active, we synthesize a keyword command so the
     *  user can speak just the VALUE ("Khalil" instead of "payee Khalil"). */
    const buildCommandText = (raw: string): string => {
        const t = raw.trim();
        if (!t) return t;
        switch (activeMode) {
            case 'date':    return (isRTL ? 'التاريخ ' : 'date ')        + t;
            case 'voucher': return (isRTL ? 'رقم السند ' : 'voucher ')   + t;
            case 'payee':   return (isRTL ? 'المستفيد ' : 'payee ')      + t;
            case 'notes':   return (isRTL ? 'ملاحظات ' : 'notes ')       + t;
            case 'amount':  return (isRTL ? 'مبلغ ' : 'amount ')         + t;
            case 'account': return t; // active line is implicit; goes through "short phrase" path
            default:        return t;
        }
    };

    const applySmartParse = async () => {
        if (!smartText.trim()) {
            toast.warning(isRTL ? 'اكتب جملة الإدخال' : 'Enter input text');
            return;
        }

        // CONTROL COMMANDS FIRST — and try several slices of the transcript.
        // Web Speech accumulates text across utterances, so "ضع المستفيد"
        // can end up as the TAIL of a longer buffer like "احمد سامي ضع المستفيد".
        // We attempt: full text → last segment → last 2..4 words. The first
        // slice that yields a control command wins.
        const CTRL_KINDS = new Set(['switchMode', 'nextLine', 'previousLine', 'newLine', 'deleteLine', 'save', 'clear']);
        const tryControl = (s: string) => {
            const c = interpretVoiceCommand(s);
            return CTRL_KINDS.has(c.kind) ? c : null;
        };
        let rawCmd = tryControl(smartText) ?? null;
        if (!rawCmd) {
            const segs = smartText.split(/[\n،,]+/).map(x => x.trim()).filter(Boolean);
            const last = segs[segs.length - 1] || '';
            if (last) rawCmd = tryControl(last);
        }
        if (!rawCmd) {
            const words = smartText.trim().split(/\s+/);
            for (let n = 2; n <= Math.min(4, words.length); n++) {
                const tail = words.slice(-n).join(' ');
                const c = tryControl(tail);
                if (c) { rawCmd = c; break; }
            }
        }
        if (!rawCmd) rawCmd = interpretVoiceCommand(smartText);
        if (rawCmd.kind === 'switchMode') {
            setActiveMode(rawCmd.target);
            clearVoiceBuffers();
            if (!voiceListening) await startVoiceCapture();
            v2Toast(isRTL ? `🎯 ${activeModeLabel(rawCmd.target)}` : `🎯 ${activeModeLabel(rawCmd.target)}`, 'info');
            return;
        }
        if (rawCmd.kind === 'nextLine') {
            const next = Math.min(activeLineIndex + 1, debitLines.length - 1);
            setActiveLineIndex(next);
            clearVoiceBuffers();
            v2Toast(isRTL ? `📍 السطر ${next + 1}` : `📍 Line ${next + 1}`, 'info');
            return;
        }
        if (rawCmd.kind === 'previousLine') {
            const prev = Math.max(activeLineIndex - 1, 0);
            setActiveLineIndex(prev);
            clearVoiceBuffers();
            v2Toast(isRTL ? `📍 السطر ${prev + 1}` : `📍 Line ${prev + 1}`, 'info');
            return;
        }
        if (rawCmd.kind === 'newLine') {
            const newLine: DebitLine = {
                id: Math.random().toString(),
                accountId: null, costCenterId: null, amount: 0, description: '',
            };
            setDebitLines(prev => {
                const next = [...prev, newLine];
                setActiveLineIndex(next.length - 1);
                return next;
            });
            clearVoiceBuffers();
            v2Toast(isRTL ? `➕ سطر جديد` : `➕ New line`, 'info');
            return;
        }
        if (rawCmd.kind === 'save') {
            clearVoiceBuffers();
            // Defer one tick so React clears the buffers before submission
            setTimeout(() => { handleSave(); }, 0);
            v2Toast(isRTL ? '💾 جاري الحفظ...' : '💾 Saving…', 'info');
            return;
        }
        if (rawCmd.kind === 'clear') {
            clearVoiceBuffers();
            // If a field-target mode is active, also wipe the value of that
            // specific control — voice "clear" should clear what the user is
            // *currently working on*, not just the transcript box.
            let cleared: string | null = null;
            if (activeMode === 'date') { setVoucherDate(''); flash('date'); cleared = activeModeLabel('date'); }
            else if (activeMode === 'voucher' || activeMode === 'notes') {
                setNotes(''); flash('notes'); cleared = activeModeLabel(activeMode);
            }
            else if (activeMode === 'payee') { setPayeeName(''); flash('payee'); cleared = activeModeLabel('payee'); }
            else if (activeMode === 'amount' && activeLineIndex < debitLines.length) {
                const id = debitLines[activeLineIndex].id;
                setDebitLines(prev => prev.map((l, i) => i === activeLineIndex ? { ...l, amount: 0 } : l));
                setAmountRaw(prev => ({ ...prev, [id]: '' }));
                flash(`line-${activeLineIndex}` as Exclude<FlashKey, null>);
                cleared = `${activeModeLabel('amount')} (${activeLineIndex + 1})`;
            }
            else if (activeMode === 'account' && activeLineIndex < debitLines.length) {
                const id = debitLines[activeLineIndex].id;
                setDebitLines(prev => prev.map((l, i) => i === activeLineIndex ? { ...l, accountId: null, costCenterId: null } : l));
                setLineAlternatives(prev => { const next = { ...prev }; delete next[id]; return next; });
                flash(`line-${activeLineIndex}` as Exclude<FlashKey, null>);
                cleared = `${activeModeLabel('account')} (${activeLineIndex + 1})`;
            }
            v2Toast(
                cleared
                    ? (isRTL ? `🗑 مُسح: ${cleared}` : `🗑 Cleared: ${cleared}`)
                    : (isRTL ? '🗑 تم المسح' : '🗑 Cleared'),
                'info'
            );
            return;
        }
        if (rawCmd.kind === 'deleteLine') {
            const idx = rawCmd.lineNumber - 1;
            if (idx < 0 || idx >= debitLines.length) {
                v2Toast(isRTL ? `السطر ${rawCmd.lineNumber} غير موجود` : `Line ${rawCmd.lineNumber} not found`, 'warning');
                return;
            }
            const target = debitLines[idx];
            setDebitLines(prev => prev.filter((_, i) => i !== idx));
            setLineAlternatives(prev => { const next = { ...prev }; delete next[target.id]; return next; });
            setActiveLineIndex(Math.max(0, Math.min(idx - 1, debitLines.length - 2)));
            v2Toast(isRTL ? `🗑️ حُذف السطر ${rawCmd.lineNumber}` : `🗑️ Deleted line ${rawCmd.lineNumber}`);
            clearVoiceBuffers();
            return;
        }

        // VALUE COMMANDS — now apply the active-mode wrapping so the spoken
        // value is routed into the currently-selected field.
        const cmd = interpretVoiceCommand(buildCommandText(smartText));

        // ── 1. Date ───────────────────────────────────────────
        if (cmd.kind === 'date') {
            const iso = parseSpokenDate(cmd.rawValue);
            if (iso) {
                setVoucherDate(iso);
                flash('date');
                v2Toast(isRTL ? `📅 تم تعيين التاريخ: ${iso}` : `📅 Date set: ${iso}`);
                clearVoiceBuffers();
                return;
            }
            // unparseable → fall through to free parse so the parser can try.
        }

        // ── 2. Voucher reference (writes to Notes as "V <number>") ──
        if (cmd.kind === 'voucherRef') {
            const ref = normalizeVoucherRef(cmd.rawValue);
            if (ref) {
                setNotes(prev => prev ? `${prev} · ${ref}` : ref);
                flash('notes');
                v2Toast(isRTL ? `🆔 تم إضافة ${ref} إلى الملاحظات` : `🆔 Added ${ref} to notes`);
                clearVoiceBuffers();
                return;
            }
        }

        // ── 3. Payee ──────────────────────────────────────────
        // We route the spoken name through /SmartEntry/parse so the backend can:
        //   - match an existing Employee (returns BOTH fullName + fullNameAr)
        //   - failing that, transliterate Arabic → English via its name table
        // Then we render the result in the UI's language (English UI shows
        // English name even if the user spoke Arabic).
        if (cmd.kind === 'payee') {
            setSmartLoading(true);
            try {
                const wrapped = (isRTL ? 'المستفيد ' : 'payee ') + cmd.rawValue;
                const { data } = await axiosInstance.post('/SmartEntry/parse', { text: wrapped });
                let resolved = cmd.rawValue;
                if (data.payeeEmployee) {
                    resolved = isRTL
                        ? (data.payeeEmployee.fullNameAr || data.payeeEmployee.fullName)
                        : (data.payeeEmployee.fullName || data.payeeEmployee.fullNameAr);
                } else if (data.payeeName) {
                    // Backend's payeeName is already language-aware (uses outputLanguage)
                    resolved = data.payeeName;
                }
                setPayeeName(resolved);
                flash('payee');
                v2Toast(isRTL ? `👤 ${resolved}` : `👤 ${resolved}`);
            } catch {
                // Network failure / 403 → fall back to raw text so user isn't blocked
                setPayeeName(cmd.rawValue);
                flash('payee');
            } finally {
                setSmartLoading(false);
            }
            clearVoiceBuffers();
            return;
        }

        // ── 4. Notes ──────────────────────────────────────────
        if (cmd.kind === 'notes') {
            setNotes(prev => prev ? `${prev} · ${cmd.rawValue}` : cmd.rawValue);
            flash('notes');
            v2Toast(isRTL ? `📝 ملاحظة مضافة` : `📝 Note added`);
            clearVoiceBuffers();
            return;
        }

        // ── 5. Delete a specific line ─────────────────────────
        if (cmd.kind === 'deleteLine') {
            const idx = cmd.lineNumber - 1;
            if (idx < 0 || idx >= debitLines.length) {
                v2Toast(isRTL ? `السطر ${cmd.lineNumber} غير موجود` : `Line ${cmd.lineNumber} not found`, 'warning');
                return;
            }
            const target = debitLines[idx];
            setDebitLines(prev => prev.filter((_, i) => i !== idx));
            // Drop its alternatives so we don't leak orphan entries
            setLineAlternatives(prev => {
                const next = { ...prev };
                delete next[target.id];
                return next;
            });
            setActiveLineIndex(Math.max(0, Math.min(idx - 1, debitLines.length - 2)));
            v2Toast(isRTL ? `🗑️ حُذف السطر ${cmd.lineNumber}` : `🗑️ Deleted line ${cmd.lineNumber}`);
            clearVoiceBuffers();
            return;
        }

        // ── 6. Amount (targeted line, or active line if not specified) ──
        if (cmd.kind === 'amount') {
            const idx = (cmd.lineNumber ? cmd.lineNumber - 1 : activeLineIndex);
            if (idx < 0 || idx >= debitLines.length) {
                v2Toast(isRTL ? `السطر غير موجود` : `Line not found`, 'warning');
                return;
            }
            const amt = parseSpokenAmount(cmd.rawValue);
            if (amt == null) {
                v2Toast(isRTL ? `لم أتعرف على المبلغ` : `Couldn't parse amount`, 'warning');
                return;
            }
            setDebitLines(prev => prev.map((l, i) => i === idx ? { ...l, amount: amt } : l));
            flash(`line-${idx}` as Exclude<FlashKey, null>);
            v2Toast(isRTL ? `💰 السطر ${idx + 1}: ${amt}` : `💰 Line ${idx + 1}: ${amt}`);
            clearVoiceBuffers();
            return;
        }

        // ── 7. Description (targeted line, or active line) ────
        if (cmd.kind === 'description') {
            const idx = (cmd.lineNumber ? cmd.lineNumber - 1 : activeLineIndex);
            if (idx < 0 || idx >= debitLines.length) {
                v2Toast(isRTL ? `السطر غير موجود` : `Line not found`, 'warning');
                return;
            }
            setDebitLines(prev => prev.map((l, i) => i === idx ? { ...l, description: cmd.rawValue } : l));
            flash(`line-${idx}` as Exclude<FlashKey, null>);
            v2Toast(isRTL ? `📝 وصف السطر ${idx + 1} حُدّث` : `📝 Line ${idx + 1} description updated`);
            clearVoiceBuffers();
            return;
        }

        // ── 8. Pick alternative — narrow active line account by hint ──
        if (cmd.kind === 'pickAlternative') {
            const idx = activeLineIndex;
            if (idx < 0 || idx >= debitLines.length) {
                v2Toast(isRTL ? `لا يوجد سطر نشط` : `No active line`, 'warning');
                return;
            }
            const targetId = debitLines[idx].id;
            const alts = lineAlternatives[targetId] || [];
            if (alts.length === 0) {
                v2Toast(isRTL ? `لا توجد بدائل لهذا السطر` : `No alternatives available`, 'warning');
                return;
            }
            const hint = cmd.hint.toLowerCase();
            const ranked = alts
                .map(a => {
                    const haystack = (a.accountName + ' ' + a.accountNameAr + ' ' + a.accountCode).toLowerCase();
                    return { a, score: haystack.includes(hint) ? 1 : 0 };
                })
                .sort((x, y) => y.score - x.score);
            const pick = ranked[0].score > 0 ? ranked[0].a : alts[0];
            setDebitLines(prev => prev.map((l, i) => i === idx ? { ...l, accountId: pick.accountId } : l));
            flash(`line-${idx}` as Exclude<FlashKey, null>);
            v2Toast(isRTL ? `✓ اختير ${pick.accountNameAr || pick.accountName}` : `✓ Selected ${pick.accountName || pick.accountNameAr}`);
            clearVoiceBuffers();
            return;
        }

        // ── 9. Targeted line (account + amount + desc bundled) ──
        if (cmd.kind === 'line') {
            const idx = cmd.lineNumber - 1;
            const existing = debitLines.length;
            if (idx > existing) {
                const gap = idx - existing;
                const ok = window.confirm(isRTL
                    ? `السطر ${cmd.lineNumber} يحتاج إنشاء ${gap} سطور فارغة قبله. متابعة؟`
                    : `Line ${cmd.lineNumber} requires creating ${gap} empty lines before it. Continue?`);
                if (!ok) { clearVoiceBuffers(); return; }
                const blanks: DebitLine[] = Array.from({ length: gap }, () => ({
                    id: Math.random().toString(),
                    accountId: null, costCenterId: null, amount: 0, description: '',
                }));
                setDebitLines(prev => [...prev, ...blanks, {
                    id: Math.random().toString(),
                    accountId: null, costCenterId: null, amount: 0, description: '',
                }]);
            } else if (idx === existing) {
                // Append a new line
                setDebitLines(prev => [...prev, {
                    id: Math.random().toString(),
                    accountId: null, costCenterId: null, amount: 0, description: '',
                }]);
            }
            setActiveLineIndex(idx);
            // Parse the payload through the parser and apply ONLY the account
            // (and description) to this line — amounts MUST be set explicitly
            // via "مبلغ"/"amount" so a number in the account phrase doesn't
            // accidentally land in the amount field.
            if (cmd.rawValue.trim()) {
                await applyParserToLine(idx, cmd.rawValue, /*includeAmount=*/ false);
                flash(`line-${idx}` as Exclude<FlashKey, null>);
            } else {
                v2Toast(isRTL ? `📍 السطر النشط: ${cmd.lineNumber}` : `📍 Active line: ${cmd.lineNumber}`);
            }
            clearVoiceBuffers();
            return;
        }

        // ── FREE DICTATION fallback ───────────────────────────
        // Short single-phrase utterances (no commas/dashes/keywords, mostly
        // looks like one account name) land on the ACTIVE line — preserving
        // the rest of the voucher — instead of replacing everything.
        const looksLikeSinglePhrase =
            smartText.length <= 40 &&
            !/[,،;:\-\n]/.test(smartText) &&
            (smartText.match(/\s+/g)?.length ?? 0) <= 3;
        if (looksLikeSinglePhrase && debitLines.length > 0 && activeLineIndex < debitLines.length) {
            await applyParserToLine(activeLineIndex, smartText, /*includeAmount=*/ false);
            flash(`line-${activeLineIndex}` as Exclude<FlashKey, null>);
            clearVoiceBuffers();
            return;
        }
        await applyFreeParse();
        clearVoiceBuffers();
    };

    /**
     * Single-line parse: send just the payload to /SmartEntry/parse, then apply
     * the FIRST line's debitAccount + (optionally amount) + description to the
     * given index. By default we IGNORE the amount when the user dictates a
     * line — amounts must be entered via the explicit "مبلغ" / "amount" command.
     * This prevents spurious amounts ("100" in "عهدة حولي 100" was being
     * grabbed when the user only wanted the account name).
     */
    const applyParserToLine = async (idx: number, payloadText: string, includeAmount = false) => {
        setSmartLoading(true);
        try {
            const { data } = await axiosInstance.post('/SmartEntry/parse', { text: payloadText });
            const first = Array.isArray(data.lines) ? data.lines[0] : null;
            if (!first || !first.debitAccount?.accountId) {
                v2Toast(isRTL ? `لم أتعرف على الحساب` : `No account matched`, 'warning');
                return;
            }
            setDebitLines(prev => prev.map((l, i) => i === idx ? {
                ...l,
                accountId: first.debitAccount.accountId,
                costCenterId: first.costCenter?.costCenterId ?? l.costCenterId,
                amount: includeAmount && Number(first.amount) > 0 ? Number(first.amount) : l.amount,
                description: first.description || l.description,
            } : l));
            const alts = (first.accountAlternatives || []).slice(0, 5);
            setLineAlternatives(prev => ({ ...prev, [debitLines[idx]?.id ?? '']: alts }));
            v2Toast(isRTL
                ? `✓ السطر ${idx + 1}: ${first.debitAccount.accountNameAr || first.debitAccount.accountName}`
                : `✓ Line ${idx + 1}: ${first.debitAccount.accountName || first.debitAccount.accountNameAr}`);
        } catch (e: any) {
            v2Toast(e?.response?.data?.message || (isRTL ? 'فشل التحليل' : 'Parse failed'), 'warning');
        } finally {
            setSmartLoading(false);
        }
    };

    /**
     * Full free-dictation parse (same shape as V1 Smart Entry).
     * Replaces ALL voucher fields with parser output.
     */
    const applyFreeParse = async () => {
        setSmartLoading(true);
        try {
            const { data } = await axiosInstance.post('/SmartEntry/parse', { text: smartText });
            if (data.date) {
                const datePart = String(data.date).substring(0, 10);
                if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) setVoucherDate(datePart);
            }
            if (data.payeeEmployee) {
                setPayeeName(data.outputLanguage === 'ar'
                    ? (data.payeeEmployee.fullNameAr || data.payeeEmployee.fullName)
                    : (data.payeeEmployee.fullName || data.payeeEmployee.fullNameAr));
            } else if (data.payeeName) {
                setPayeeName(data.payeeName);
            }
            if (data.creditAccount?.accountId) setCreditAccountId(data.creditAccount.accountId);

            if (Array.isArray(data.lines) && data.lines.length > 0) {
                const newLines: DebitLine[] = data.lines
                    .filter((l: any) => l.debitAccount?.accountId)
                    .map((l: any) => ({
                        id: Math.random().toString(),
                        accountId: l.debitAccount.accountId,
                        costCenterId: l.costCenter?.costCenterId ?? null,
                        amount: Number(l.amount) || 0,
                        description: l.description || '',
                        accountAlternatives: (l.accountAlternatives || []).slice(0, 5),
                    }));
                if (newLines.length > 0) {
                    setDebitLines(newLines);
                    const total = newLines.reduce((s, l) => s + l.amount, 0);
                    setCreditAmount(Math.round(total * 1000) / 1000);
                    setActiveLineIndex(0);
                    // Stash alternatives keyed by new line ids.
                    const altMap: typeof lineAlternatives = {};
                    newLines.forEach((nl, i) => {
                        altMap[nl.id] = (data.lines[i]?.accountAlternatives || []).slice(0, 5);
                    });
                    setLineAlternatives(altMap);
                }
            }
            if (Array.isArray(data.notes) && data.notes.length > 0) {
                setNotes(data.notes.join(' · '));
            }
            v2Toast(isRTL
                ? `تم ملء السند — ${data.lines?.length || 0} سطر`
                : `Voucher filled — ${data.lines?.length || 0} line(s)`);
            setSmartOpen(false);
            setSmartText('');
        } catch (e: any) {
            const status = e?.response?.status;
            const msg = status === 403
                ? (isRTL ? 'مرفوض — للسوبر أدمن فقط' : 'Forbidden — Super Admin only')
                : (e?.response?.data?.message || (isRTL ? 'فشل التحليل' : 'Parse failed'));
            toast.error(msg);
        } finally {
            setSmartLoading(false);
        }
    };

    const generateAutoNumber = async () => {
        try {
            const year = new Date().getFullYear();
            const vouchers = await cashPaymentsService.getAll();
            const currentYearVouchers = vouchers.filter((v) =>
                v.voucherNumber.startsWith(`CP-${year}-`)
            );
            let maxNum = 0;
            currentYearVouchers.forEach((v) => {
                const parts = v.voucherNumber.split('-');
                if (parts.length === 3) {
                    const num = parseInt(parts[2]);
                    if (!isNaN(num) && num > maxNum) maxNum = num;
                }
            });
            const nextNum = (maxNum + 1).toString().padStart(4, '0');
            return `CP-${year}-${nextNum}`;
        } catch (err) {
            return `CP-${new Date().getFullYear()}-0001`;
        }
    };

    const checkVoucherNumberExists = async (
        number: string
    ): Promise<boolean> => {
        try {
            const vouchers = await cashPaymentsService.getAll();
            return vouchers.some(
                (v) =>
                    v.voucherNumber === number &&
                    v.voucherId !== editVoucher?.voucherId
            );
        } catch (err) {
            return false;
        }
    };

    const resetForm = () => {
        setVoucherNumber('');
        setVoucherDate(new Date().toISOString().split('T')[0]);
        setNotes('');
        setPayeeName('');
        setCreditAccountId(null);
        setCreditAmount(0);
        setDebitLines([
            {
                id: Math.random().toString(),
                accountId: null,
                costCenterId: null,
                amount: 0,
                description: '',
            },
        ]);
        setError('');
    };

    const loadEditData = (voucher: VoucherDto) => {
        setVoucherNumber(voucher.voucherNumber);
        setVoucherDate(voucher.voucherDate.split('T')[0]);
        setNotes(voucher.notes || '');
        setPayeeName(voucher.payeeName || '');
        if (!voucher.details || voucher.details.length === 0) {
            setError(
                isRTL
                    ? 'لا توجد تفاصيل في السند'
                    : 'No details found in voucher'
            );
            return;
        }
        const creditDetail = voucher.details.find(
            (d) => d.side?.toLowerCase() === VOUCHER_SIDE.CREDIT.toLowerCase()
        );
        if (creditDetail) {
            setCreditAccountId(creditDetail.accountId || null);
            setCreditAmount(creditDetail.allocatedAmount);
        }
        const debitDetails = voucher.details.filter(
            (d) => d.side?.toLowerCase() === VOUCHER_SIDE.DEBIT.toLowerCase()
        );
        if (debitDetails.length > 0) {
            setDebitLines(
                debitDetails.map((d) => ({
                    id: Math.random().toString(),
                    accountId: d.accountId || null,
                    costCenterId: d.costCenterId || null,
                    amount: d.allocatedAmount,
                    description: d.description || '',
                }))
            );
        }
    };

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        if (open) {
            if (editVoucher) {
                timer = setTimeout(() => loadEditData(editVoucher), 100);
            } else {
                resetForm();
                generateAutoNumber().then(setVoucherNumber);
                // V2: pre-fill credit account with the first cash box under
                // cashParentAccountId so the user doesn't have to pick one.
                // Falls back to "Safe Box Main Cash" (code 01-01-01-001) if
                // available, otherwise just picks the first child.
                if (cashParentAccountId) {
                    chartOfAccountsService.getByParentId(cashParentAccountId).then(rows => {
                        const finals = rows.filter(r => r.isFinalAccount);
                        const main = finals.find(r => r.accountCode === '01-01-01-001') || finals[0];
                        if (main) setCreditAccountId(main.accountId);
                    }).catch(() => { /* non-fatal */ });
                }
            }
        }
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editVoucher?.voucherId, cashParentAccountId]);

    const addDebitLine = () =>
        setDebitLines([
            ...debitLines,
            {
                id: Math.random().toString(),
                accountId: null,
                costCenterId: null,
                amount: 0,
                description: '',
            },
        ]);
    const removeDebitLine = (id: string) => {
        if (debitLines.length > 1)
            setDebitLines(debitLines.filter((line) => line.id !== id));
    };
    const updateDebitLine = (id: string, field: keyof DebitLine, value: any) =>
        setDebitLines(
            debitLines.map((line) =>
                line.id === id ? { ...line, [field]: value } : line
            )
        );
    const isAccountUsed = (
        accountId: number | null,
        currentLineId?: string
    ): boolean => {
        if (!accountId) return false;
        if (accountId === creditAccountId) return true;
        return debitLines.some(
            (line) => line.id !== currentLineId && line.accountId === accountId
        );
    };

    const totalDebit = Math.round(
        debitLines.reduce((sum, line) => sum + (line.amount || 0), 0) * 1000
    ) / 1000;
    const totalCredit = creditAmount || 0;
    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
    const difference = totalDebit - totalCredit;

    useEffect(() => {
        setCreditAmount(totalDebit);
    }, [totalDebit]);

    const validate = async (): Promise<boolean> => {
        if (!voucherNumber.trim()) {
            setError(isRTL ? 'رقم السند مطلوب' : 'Voucher number is required');
            return false;
        }
        setCheckingNumber(true);
        const exists = await checkVoucherNumberExists(voucherNumber);
        setCheckingNumber(false);
        if (exists) {
            setError(
                isRTL
                    ? 'رقم السند مستخدم مسبقاً'
                    : 'Voucher number already exists'
            );
            return false;
        }
        if (!voucherDate) {
            setError(isRTL ? 'التاريخ مطلوب' : 'Date is required');
            return false;
        }
        if (!payeeName.trim()) {
            setError(isRTL ? 'اسم المستفيد مطلوب' : 'Payee name is required');
            return false;
        }
        if (!creditAccountId) {
            setError(isRTL ? 'الصندوق مطلوب' : 'Cash account is required');
            return false;
        }
        if (totalCredit <= 0) {
            setError(
                isRTL
                    ? 'المبلغ يجب أن يكون أكبر من صفر'
                    : 'Amount must be greater than zero'
            );
            return false;
        }
        if (!isBalanced) {
            setError(
                isRTL ? 'المدين يجب أن يساوي الدائن' : 'Debit must equal Credit'
            );
            return false;
        }
        const invalidDebitLines = debitLines.filter(
            (line) => !line.accountId || line.amount <= 0
        );
        if (invalidDebitLines.length > 0) {
            setError(
                isRTL
                    ? 'جميع الأسطر يجب أن تحتوي على حساب ومبلغ'
                    : 'All lines must have account and amount'
            );
            return false;
        }
        for (const line of debitLines) {
            if (line.accountId) {
                try {
                    const accountResponse = await apiClient.get(
                        '/ChartOfAccounts/' + line.accountId
                    );
                    const account = accountResponse.data.data;
                    if (
                        account &&
                        account.requireCostCenter &&
                        !line.costCenterId
                    ) {
                        const msg =
                            (isRTL ? 'الحساب' : 'Account') +
                            ' "' +
                            (isRTL
                                ? account.accountNameAr
                                : account.accountName) +
                            '" ' +
                            (isRTL
                                ? 'يتطلب مركز تكلفة'
                                : 'requires Cost Center');
                        setError(msg);
                        toast.warning(msg);
                        return false;
                    }
                } catch (err) {
                    console.error('Error validating account:', err);
                }
            }
        }

        setError('');
        return true;
    };

    const handleSave = async () => {
        if (!(await validate())) return;
        // V2: stop any in-flight voice capture BEFORE the network call so the
        // mic indicator/icon doesn't linger after the dialog closes. The
        // useEffect on `open` is a safety net but state updates race the
        // network response — stopping here is the deterministic path.
        if (voiceListening) stopVoiceCapture();
        setActiveMode(null);
        try {
            setLoading(true);
            setError('');
            const details: CreateVoucherDetailDto[] = [];
            details.push({
                allocatedAmount: creditAmount,
                accountId: creditAccountId,
                side: VOUCHER_SIDE.CREDIT,
                costCenterId: null,
                description: notes,
            });
            debitLines.forEach((line) =>
                details.push({
                    allocatedAmount: line.amount,
                    accountId: line.accountId,
                    side: VOUCHER_SIDE.DEBIT,
                    costCenterId: line.costCenterId,
                    description: line.description,
                })
            );
            if (editVoucher) {
                await onSave({
                    voucherId: editVoucher.voucherId,
                    voucherNumber,
                    voucherDate,
                    voucherType: 'CP',
                    amount: totalCredit,
                    currencyCode: 'KWD',
                    exchangeRate: 1,
                    notes,
                    payeeName,
                    details,
                });
            } else {
                await onSave({
                    voucherNumber,
                    voucherDate,
                    voucherType: 'CP',
                    amount: totalCredit,
                    currencyCode: 'KWD',
                    exchangeRate: 1,
                    notes,
                    payeeName,
                    details,
                });
            }
            toast.success(isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully');
            handleClose();
        } catch (err: any) {
            const errorMsg =
                err.message || (isRTL ? 'حدث خطأ' : 'An error occurred');
            setError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen
            dir={isRTL ? 'rtl' : 'ltr'}
            PaperProps={{
                sx: {
                    background: isDarkMode
                        ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
                        : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    border: isDarkMode
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                },
            }}
        >
            <DialogTitle
                sx={{
                    background: isDarkMode
                        ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2,
                    borderBottom: isDarkMode
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <MonetizationOnIcon sx={{ fontSize: 32 }} />
                    <Typography variant="h5" fontWeight="bold">
                        {editVoucher
                            ? isRTL
                                ? 'تعديل سند صرف نقدي'
                                : 'Edit Cash Payment'
                            : isRTL
                              ? 'سند صرف نقدي جديد'
                              : 'New Cash Payment'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!editVoucher && isSuperAdmin && (
                        <Button
                            variant="contained"
                            startIcon={<AutoFixHighIcon />}
                            onClick={() => setSmartOpen(true)}
                            sx={{
                                background: 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)',
                                color: 'white',
                                fontWeight: 'bold',
                                '&:hover': { background: 'linear-gradient(135deg, #6a3de8 0%, #9c70f5 100%)' },
                            }}
                        >
                            {isRTL ? '🪄 إدخال ذكي' : '🪄 Smart Entry'}
                        </Button>
                    )}
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Smart Entry sub-dialog */}
            <Dialog open={smartOpen} onClose={() => setSmartOpen(false)} maxWidth="md" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
                <DialogTitle sx={{ background: 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)', color: 'white' }}>
                    🪄 {isRTL ? 'الإدخال الذكي بالنص الطبيعي' : 'Smart Entry — Natural Text'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {isRTL
                            ? 'اكتب جملة طبيعية أو اضغط 🎤 وتكلّم بصوتك. النظام يحلّل ويملأ الحقول. لن يُحفظ السند إلا بعد مراجعتك وضغط Save.'
                            : 'Type natural text or press 🎤 and speak. The system parses and fills the form. Nothing is saved until you review and click Save.'}
                    </Alert>

                    <Stack direction="row" spacing={1} mb={1.5} alignItems="center" flexWrap="wrap">
                        {voiceListening ? (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<MicOffIcon />}
                                onClick={stopVoiceCapture}
                                sx={{ textTransform: 'none' }}
                            >
                                {isRTL ? '⏹ إيقاف الاستماع' : '⏹ Stop Listening'}
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                startIcon={<MicIcon />}
                                disabled={smartLoading || !isSpeechSupported()}
                                onClick={startVoiceCapture}
                                sx={{ textTransform: 'none' }}
                            >
                                {isRTL ? '🎤 إدخال صوتي' : '🎤 Voice Input'}
                            </Button>
                        )}
                        {/* Language toggle for voice recognition */}
                        {!voiceListening && isSpeechSupported() && (
                            <Stack direction="row" spacing={0.5}>
                                <Chip
                                    size="small"
                                    label="عربي"
                                    color={voiceLang === 'ar-SA' ? 'primary' : 'default'}
                                    onClick={() => setVoiceLang('ar-SA')}
                                    sx={{ cursor: 'pointer' }}
                                />
                                <Chip
                                    size="small"
                                    label="EN"
                                    color={voiceLang === 'en-US' ? 'primary' : 'default'}
                                    onClick={() => setVoiceLang('en-US')}
                                    sx={{ cursor: 'pointer' }}
                                />
                            </Stack>
                        )}
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => { setSmartText(''); voiceRawRef.current = ''; }}
                            sx={{ textTransform: 'none' }}
                        >
                            {isRTL ? '🗑 مسح النص' : '🗑 Clear'}
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            onClick={async () => {
                                if (!smartText) {
                                    toast.info(isRTL ? 'لا يوجد نص للنسخ' : 'No text to copy');
                                    return;
                                }
                                try {
                                    await navigator.clipboard.writeText(smartText);
                                    toast.success(isRTL ? 'تم نسخ النص بالكامل' : 'Text copied to clipboard');
                                } catch {
                                    // Fallback for older browsers
                                    const ta = document.createElement('textarea');
                                    ta.value = smartText;
                                    document.body.appendChild(ta);
                                    ta.select();
                                    try { document.execCommand('copy'); toast.success(isRTL ? 'تم النسخ' : 'Copied'); }
                                    catch { toast.error(isRTL ? 'فشل النسخ' : 'Copy failed'); }
                                    document.body.removeChild(ta);
                                }
                            }}
                            sx={{ textTransform: 'none' }}
                        >
                            {isRTL ? '📋 نسخ النص' : '📋 Copy'}
                        </Button>
                        {!isSpeechSupported() && (
                            <Typography variant="caption" color="text.secondary">
                                {isRTL
                                    ? '(الإدخال الصوتي يتطلب Chrome أو Edge)'
                                    : '(Voice input requires Chrome or Edge)'}
                            </Typography>
                        )}
                    </Stack>

                    {voiceListening && (
                        <Box sx={{
                            mb: 1.5,
                            p: 2,
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: audioLevel > 10 ? 'success.main' : 'warning.main',
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                        }}>
                            {/* Audio level meter — visual proof the mic is being picked up */}
                            <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                                <MicIcon color={audioLevel > 10 ? 'success' : 'warning'} />
                                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90 }}>
                                    {audioLevel > 10
                                        ? (isRTL ? 'الميكروفون يعمل' : 'Mic working')
                                        : (isRTL ? 'لا يُسمع صوت' : 'No audio')}
                                </Typography>
                                {/* Animated bar */}
                                <Box sx={{
                                    flex: 1,
                                    height: 18,
                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}>
                                    <Box sx={{
                                        height: '100%',
                                        width: `${audioLevel}%`,
                                        background: audioLevel > 50
                                            ? 'linear-gradient(90deg, #4caf50, #ff9800)'
                                            : audioLevel > 10
                                                ? 'linear-gradient(90deg, #4caf50, #8bc34a)'
                                                : '#9e9e9e',
                                        transition: 'width 0.08s linear',
                                    }} />
                                </Box>
                                <Typography variant="caption" sx={{ minWidth: 32, textAlign: 'end' }}>
                                    {audioLevel}
                                </Typography>
                            </Stack>

                            {/* Live transcript — LARGE font, easy to read on mobile */}
                            <Box sx={{
                                minHeight: 60,
                                p: 1.5,
                                bgcolor: isDarkMode ? 'rgba(0,0,0,0.3)' : 'white',
                                borderRadius: 1,
                                border: '1px dashed',
                                borderColor: 'divider',
                            }}>
                                <Typography sx={{
                                    fontSize: { xs: '1.4rem', sm: '1.5rem' },
                                    fontWeight: 500,
                                    color: voiceInterim ? 'primary.main' : 'text.disabled',
                                    lineHeight: 1.5,
                                    direction: voiceLang === 'ar-SA' ? 'rtl' : 'ltr',
                                    textAlign: voiceLang === 'ar-SA' ? 'right' : 'left',
                                }}>
                                    {voiceInterim || (isRTL ? '🎙 تكلّم بصوت واضح...' : '🎙 Speak clearly...')}
                                </Typography>
                            </Box>

                            {audioLevel < 5 && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'warning.main' }}>
                                    {isRTL
                                        ? '⚠ إذا لم يتحرك المؤشر بعد 3 ثوانٍ من الكلام، الميكروفون معطّل في إعدادات Windows.'
                                        : '⚠ If the meter stays flat after 3s of speaking, mic is disabled in Windows settings.'}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={12}
                        autoFocus
                        value={smartText}
                        onChange={e => { setSmartText(e.target.value); voiceRawRef.current = e.target.value; }}
                        placeholder={isRTL
                            ? 'اكتب هنا أو تكلّم بصوتك...'
                            : 'Type or speak...'}
                        helperText={isRTL
                            ? 'مثال صوتي: "المستفيد شركة العربي، سطر جديد، صيانة الأفنيوز، سطر جديد، 475، سطر جديد، في 6322، سطر جديد، التاريخ 3 3 2026"'
                            : 'Voice example: "Paid to Al-Arabi Company, new line, Avenues maintenance, new line, 475, new line, voucher 6322, new line, date 3 3 2026"'}
                    />
                    <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
                        {[
                            'مخصص اجازات المنقف 184.600 Sayed Abdul Sattar 1-2-2026 V 6283',
                            'عهدة سعد 250 لعبدالمعين',
                            'camp maintenance 90 robin',
                            'صرف 25.9 كمبيوتر ، 33.5 قرطاسية ، 32.6 ضيافة',
                        ].map((s, i) => (
                            <Chip key={i} size="small" label={s} onClick={() => setSmartText(s)} sx={{ cursor: 'pointer' }} />
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setSmartOpen(false)} disabled={smartLoading}>
                        {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={applySmartParse}
                        disabled={smartLoading}
                        startIcon={smartLoading ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
                        sx={{ background: 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)' }}
                    >
                        {isRTL ? 'تحليل وملء السند' : 'Parse & Fill'}
                    </Button>
                </DialogActions>
            </Dialog>

            <DialogContent sx={{ mt: 1, overflowY: 'auto', px: { xs: 1.5, sm: 2, md: 3 } }}>
                <Stack spacing={1.5}>
                    {/* ═══════════════════════════════════════════════════
                        V2 STICKY LIVE VOICE BAR — always visible on top of
                        the dialog so the user sees the live transcript and
                        can dictate without opening any sub-dialog.
                        ═══════════════════════════════════════════════════ */}
                    {isSuperAdmin && !editVoucher && (
                        <Paper
                            elevation={3}
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                p: 1.25,
                                background: voiceListening
                                    ? 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)'
                                    : 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)',
                                color: 'white',
                                borderRadius: 2,
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
                                <Button
                                    variant="contained"
                                    size="large"
                                    color={voiceListening ? 'error' : 'success'}
                                    startIcon={voiceListening ? <MicOffIcon /> : <MicIcon />}
                                    onClick={() => (voiceListening ? stopVoiceCapture() : startVoiceCapture())}
                                    sx={{ fontWeight: 'bold', minWidth: 180 }}
                                >
                                    {voiceListening
                                        ? (isRTL ? '⏹ إيقاف' : '⏹ Stop')
                                        : (isRTL ? '🎤 ابدأ النطق' : '🎤 Start Voice')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => setVoiceLang(voiceLang === 'ar-SA' ? 'en-US' : 'ar-SA')}
                                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
                                >
                                    {voiceLang === 'ar-SA' ? 'AR 🇰🇼' : 'EN 🇬🇧'}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AutoFixHighIcon />}
                                    disabled={!smartText.trim() || smartLoading}
                                    onClick={applySmartParse}
                                    sx={{ fontWeight: 'bold', bgcolor: 'white', color: '#7c4dff', '&:hover': { bgcolor: 'grey.100' } }}
                                >
                                    {smartLoading
                                        ? (isRTL ? 'يحلل...' : 'Parsing…')
                                        : (isRTL ? '🚀 طبّق' : '🚀 Apply')}
                                </Button>
                                <Button
                                    variant="text"
                                    onClick={() => { setSmartText(''); voiceRawRef.current = ''; setVoiceInterim(''); }}
                                    sx={{ color: 'white' }}
                                >
                                    {isRTL ? '🗑 مسح' : '🗑 Clear'}
                                </Button>
                            </Stack>

                            {/* MODE BUTTONS — press once to lock the next utterance into a field.
                                The active button glows; press again (or another) to switch. */}
                            <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                                {([
                                    { mode: 'date' as const,    lbl: '📅', name: isRTL ? 'التاريخ'    : 'date' },
                                    { mode: 'voucher' as const, lbl: '🆔', name: isRTL ? 'رقم السند' : 'voucher' },
                                    { mode: 'payee' as const,   lbl: '👤', name: isRTL ? 'المستفيد'  : 'payee' },
                                    { mode: 'notes' as const,   lbl: '📝', name: isRTL ? 'ملاحظات'   : 'notes' },
                                    { mode: 'account' as const, lbl: '🏦', name: isRTL ? 'الحساب'    : 'account' },
                                    { mode: 'amount' as const,  lbl: '💰', name: isRTL ? 'المبلغ'    : 'amount' },
                                ]).map(b => {
                                    const isActive = activeMode === b.mode;
                                    return (
                                        <Chip
                                            key={b.mode}
                                            label={`${b.lbl} ${b.name}`}
                                            onClick={async () => {
                                                // Toggle the mode and AUTO-START listening so the user
                                                // can dictate the value immediately. If already in this
                                                // mode, pressing again deactivates and stops voice.
                                                if (activeMode === b.mode) {
                                                    setActiveMode(null);
                                                    clearVoiceBuffers();
                                                    if (voiceListening) stopVoiceCapture();
                                                } else {
                                                    setActiveMode(b.mode);
                                                    clearVoiceBuffers();
                                                    if (!voiceListening) await startVoiceCapture();
                                                }
                                            }}
                                            sx={{
                                                bgcolor: isActive ? '#fbbf24' : 'rgba(255,255,255,0.18)',
                                                color: isActive ? '#000' : 'white',
                                                fontWeight: isActive ? 'bold' : 'normal',
                                                cursor: 'pointer',
                                                border: isActive ? '2px solid #fff' : '2px solid transparent',
                                                boxShadow: isActive ? '0 0 10px rgba(251,191,36,0.7)' : 'none',
                                                '&:hover': { bgcolor: isActive ? '#fbbf24' : 'rgba(255,255,255,0.35)' },
                                            }}
                                        />
                                    );
                                })}
                            </Stack>

                            {/* LINE SELECT — highlights the chosen line; voice value lands there */}
                            <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap alignItems="center">
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', mr: 0.5 }}>
                                    {isRTL ? 'السطر النشط:' : 'Active line:'}
                                </Typography>
                                {debitLines.map((_, i) => {
                                    const isActive = i === activeLineIndex;
                                    return (
                                        <Chip
                                            key={i}
                                            label={`${i + 1}`}
                                            onClick={() => {
                                                setActiveLineIndex(i);
                                                v2Toast(isRTL ? `📍 السطر النشط: ${i + 1}` : `📍 Active line: ${i + 1}`, 'info');
                                            }}
                                            sx={{
                                                bgcolor: isActive ? '#10b981' : 'rgba(255,255,255,0.18)',
                                                color: 'white',
                                                fontWeight: isActive ? 'bold' : 'normal',
                                                minWidth: 36,
                                                cursor: 'pointer',
                                                border: isActive ? '2px solid #fff' : '2px solid transparent',
                                                boxShadow: isActive ? '0 0 8px rgba(16,185,129,0.7)' : 'none',
                                                '&:hover': { bgcolor: isActive ? '#10b981' : 'rgba(255,255,255,0.3)' },
                                            }}
                                        />
                                    );
                                })}
                                {/* Add-new-line button: appends a blank row + makes it active */}
                                <Chip
                                    label={isRTL ? '➕ سطر جديد' : '➕ New line'}
                                    onClick={() => {
                                        const newLine: DebitLine = {
                                            id: Math.random().toString(),
                                            accountId: null, costCenterId: null, amount: 0, description: '',
                                        };
                                        setDebitLines(prev => {
                                            const next = [...prev, newLine];
                                            setActiveLineIndex(next.length - 1);
                                            return next;
                                        });
                                        v2Toast(isRTL ? `📍 سطر ${debitLines.length + 1} جديد` : `📍 Line ${debitLines.length + 1} added`, 'info');
                                    }}
                                    sx={{
                                        bgcolor: 'rgba(16,185,129,0.6)',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        border: '2px dashed rgba(255,255,255,0.5)',
                                        '&:hover': { bgcolor: 'rgba(16,185,129,0.85)' },
                                    }}
                                />
                            </Stack>

                            {/* Active-mode banner */}
                            {activeMode && (
                                <Box
                                    sx={{
                                        mb: 1,
                                        p: 1,
                                        borderRadius: 1,
                                        bgcolor: 'rgba(251,191,36,0.25)',
                                        border: '1px dashed #fbbf24',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: '#fbbf24', fontWeight: 'bold', flex: 1 }}>
                                        {isRTL
                                            ? `🎯 ضع في: ${activeModeLabel(activeMode)}${activeMode === 'amount' || activeMode === 'account' ? ` (السطر ${activeLineIndex + 1})` : ''}`
                                            : `🎯 Targeting: ${activeModeLabel(activeMode)}${activeMode === 'amount' || activeMode === 'account' ? ` (Line ${activeLineIndex + 1})` : ''}`}
                                    </Typography>
                                    <Button size="small" onClick={() => setActiveMode(null)} sx={{ color: 'white', minWidth: 0 }}>
                                        ❌
                                    </Button>
                                </Box>
                            )}

                            {/* LIVE TRANSCRIPT — compact but always visible */}
                            <Box
                                sx={{
                                    bgcolor: 'rgba(0,0,0,0.25)',
                                    color: 'white',
                                    minHeight: 44,
                                    p: 1,
                                    borderRadius: 1.5,
                                    fontSize: '1.05rem',
                                    fontWeight: 'medium',
                                    direction: isRTL ? 'rtl' : 'ltr',
                                    border: voiceListening ? '2px solid #fbbf24' : '2px solid transparent',
                                    boxShadow: voiceListening ? '0 0 12px rgba(251,191,36,0.6)' : 'none',
                                }}
                                onClick={() => {
                                    // Tap the transcript to edit it manually
                                    const newText = window.prompt(isRTL ? 'تعديل النص:' : 'Edit text:', smartText);
                                    if (newText != null) { setSmartText(newText); voiceRawRef.current = newText; }
                                }}
                            >
                                {smartText || voiceInterim ? (
                                    <>
                                        <Box component="span">{smartText}</Box>
                                        {voiceInterim && (
                                            <Box component="span" sx={{ opacity: 0.6, fontStyle: 'italic' }}>
                                                {smartText ? ' ' : ''}{voiceInterim}
                                            </Box>
                                        )}
                                    </>
                                ) : (
                                    <Box component="span" sx={{ opacity: 0.6 }}>
                                        {voiceListening
                                            ? (isRTL ? '🎙 يستمع... تكلم الآن (مثال: المستفيد خليل قاسم)' : '🎙 Listening… speak now (e.g., "payee John")')
                                            : (isRTL ? 'اضغط 🎤 أو اكتب أمراً صوتياً ثم 🚀 طبّق' : 'Press 🎤 or type a command then 🚀 Apply')}
                                    </Box>
                                )}
                            </Box>

                            {voiceListening && (
                                <Box sx={{ mt: 1, height: 4, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, overflow: 'hidden' }}>
                                    <Box sx={{
                                        height: '100%',
                                        width: `${Math.min(100, audioLevel)}%`,
                                        bgcolor: '#10b981',
                                        transition: 'width 0.1s linear',
                                    }} />
                                </Box>
                            )}
                        </Paper>
                    )}

                    {error && (
                        <Alert
                            severity="error"
                            onClose={() => setError('')}
                            sx={{ borderRadius: 2 }}
                        >
                            {error}
                        </Alert>
                    )}

                    <Card
                        elevation={3}
                        sx={{
                            borderRadius: 2,
                            bgcolor: isDarkMode
                                ? 'rgba(255,255,255,0.05)'
                                : 'white',
                            border: isDarkMode
                                ? '1px solid rgba(255,255,255,0.1)'
                                : 'none',
                        }}
                    >
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                    color: isDarkMode
                                        ? '#90caf9'
                                        : 'primary.main',
                                    fontWeight: 'bold',
                                }}
                            >
                                {isRTL
                                    ? 'معلومات السند'
                                    : 'Voucher Information'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                <TextField
                                    label={
                                        isRTL ? 'رقم السند' : 'Voucher Number'
                                    }
                                    value={voucherNumber}
                                    onChange={(e) =>
                                        setVoucherNumber(e.target.value)
                                    }
                                    fullWidth
                                    required
                                    placeholder="CP-2026-0001"
                                    InputProps={{
                                        endAdornment: !editVoucher && (
                                            <IconButton
                                                size="small"
                                                onClick={async () =>
                                                    setVoucherNumber(
                                                        await generateAutoNumber()
                                                    )
                                                }
                                                title={
                                                    isRTL
                                                        ? 'إعادة توليد الرقم'
                                                        : 'Regenerate Number'
                                                }
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        ),
                                    }}
                                    sx={{
                                        bgcolor: isDarkMode
                                            ? 'rgba(255,255,255,0.05)'
                                            : 'white',
                                        '& .MuiInputLabel-root': {
                                            color: isDarkMode
                                                ? 'rgba(255,255,255,0.7)'
                                                : 'inherit',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            color: isDarkMode
                                                ? 'white'
                                                : 'inherit',
                                            '& fieldset': {
                                                borderColor: isDarkMode
                                                    ? 'rgba(255,255,255,0.23)'
                                                    : 'inherit',
                                            },
                                        },
                                        ...flashSx('voucher'),
                                    }}
                                />
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label={isRTL ? 'التاريخ' : 'Date'}
                                        value={voucherDate ? new Date(voucherDate) : null}
                                        onChange={(date) => setVoucherDate(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                                        format="dd/MM/yyyy"
                                        views={['year', 'month', 'day']}
                                        openTo="day"
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        slotProps={{ textField: { fullWidth: true, required: true, sx: { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'white', '& .MuiInputLabel-root': { color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'inherit' }, '& .MuiOutlinedInput-root': { color: isDarkMode ? 'white' : 'inherit', '& fieldset': { borderColor: isDarkMode ? 'rgba(255,255,255,0.23)' : 'inherit' } }, ...flashSx('date') } } }}
                                    />
                                </LocalizationProvider>
                            </Box>
                            <TextField
                                label={isRTL ? 'اسم المستفيد' : 'Payee Name'}
                                value={payeeName}
                                onChange={(e) => setPayeeName(e.target.value)}
                                fullWidth
                                required
                                placeholder={
                                    isRTL
                                        ? 'أدخل اسم المستفيد'
                                        : 'Enter payee name'
                                }
                                InputProps={{
                                    startAdornment: (
                                        <PersonIcon
                                            sx={{
                                                mr: 1,
                                                color: isDarkMode
                                                    ? 'rgba(255,255,255,0.5)'
                                                    : 'action.active',
                                            }}
                                        />
                                    ),
                                }}
                                sx={{
                                    mt: 2,
                                    bgcolor: isDarkMode
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'white',
                                    '& .MuiInputLabel-root': {
                                        color: isDarkMode
                                            ? 'rgba(255,255,255,0.7)'
                                            : 'inherit',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        color: isDarkMode ? 'white' : 'inherit',
                                        '& fieldset': {
                                            borderColor: isDarkMode
                                                ? 'rgba(255,255,255,0.23)'
                                                : 'inherit',
                                        },
                                    },
                                    ...flashSx('payee'),
                                }}
                            />
                            <TextField
                                label={isRTL ? 'ملاحظات' : 'Notes'}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{
                                    mt: 2,
                                    bgcolor: isDarkMode
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'white',
                                    ...flashSx('notes'),
                                    '& .MuiInputLabel-root': {
                                        color: isDarkMode
                                            ? 'rgba(255,255,255,0.7)'
                                            : 'inherit',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        color: isDarkMode ? 'white' : 'inherit',
                                        '& fieldset': {
                                            borderColor: isDarkMode
                                                ? 'rgba(255,255,255,0.23)'
                                                : 'inherit',
                                        },
                                    },
                                }}
                            />
                        </CardContent>
                    </Card>

                    <Card
                        elevation={4}
                        sx={{
                            borderRadius: 2,
                            background: isDarkMode
                                ? 'linear-gradient(135deg, rgba(255,152,0,0.15) 0%, rgba(255,193,7,0.15) 100%)'
                                : 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                            border: '2px solid',
                            borderColor: isDarkMode
                                ? 'rgba(255,152,0,0.5)'
                                : 'warning.main',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <AccountBalanceWalletIcon
                                    sx={{
                                        color: isDarkMode
                                            ? '#ffb74d'
                                            : 'warning.dark',
                                        fontSize: 28,
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        color: isDarkMode
                                            ? '#ffb74d'
                                            : 'warning.dark',
                                    }}
                                >
                                    {isRTL
                                        ? '📤 الطرف الدائن (من الصندوق)'
                                        : '📤 Credit Side (From Cash)'}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <AccountSelector
                                        value={creditAccountId}
                                        onChange={setCreditAccountId}
                                        label={
                                            isRTL ? 'الصندوق' : 'Cash Account'
                                        }
                                        required
                                        parentAccountId={cashParentAccountId}
                                        onlyFinalAccounts={true}
                                    />
                                </Box>
                                <TextField
                                    label={isRTL ? 'المبلغ' : 'Amount'}
                                    type="text"
                                    inputMode="decimal"
                                    value={creditAmount ? creditAmount.toFixed(3) : '0.000'}
                                    disabled
                                    sx={{
                                        width: 200,
                                        '& .MuiInputBase-root': {
                                            bgcolor: isDarkMode
                                                ? 'rgba(255,255,255,0.1)'
                                                : 'rgba(255,255,255,0.9)',
                                            color: isDarkMode
                                                ? '#ffb74d'
                                                : 'inherit',
                                            fontWeight: 'bold',
                                            fontSize: '1.1rem',
                                        },
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    <Card
                        elevation={4}
                        sx={{
                            borderRadius: 2,
                            background: isDarkMode
                                ? 'linear-gradient(135deg, rgba(76,175,80,0.15) 0%, rgba(129,199,132,0.15) 100%)'
                                : 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                            border: '2px solid',
                            borderColor: isDarkMode
                                ? 'rgba(76,175,80,0.5)'
                                : 'success.main',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <TrendingUpIcon
                                        sx={{
                                            color: isDarkMode
                                                ? '#81c784'
                                                : 'success.dark',
                                            fontSize: 28,
                                        }}
                                    />
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{
                                            color: isDarkMode
                                                ? '#81c784'
                                                : 'success.dark',
                                        }}
                                    >
                                        {isRTL
                                            ? '📥 الطرف المدين (إلى)'
                                            : '📥 Debit Side (To)'}
                                    </Typography>
                                </Box>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={addDebitLine}
                                    variant="contained"
                                    size="small"
                                    sx={{
                                        background: isDarkMode
                                            ? 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)'
                                            : 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                        boxShadow:
                                            '0 3px 5px 2px rgba(33, 203, 243, .3)',
                                    }}
                                >
                                    {isRTL ? 'إضافة سطر' : 'Add Line'}
                                </Button>
                            </Box>
                            <Box ref={gridRef}>
                            <Stack spacing={2}>
                                {debitLines.map((line, index) => {
                                    const isFlashing = flashField === `line-${index}`;
                                    return (
                                    <Paper
                                        key={line.id}
                                        ref={(el: HTMLDivElement | null) => { lineRefs.current[index] = el; }}
                                        elevation={index === activeLineIndex || isFlashing ? 6 : 2}
                                        onClick={() => setActiveLineIndex(index)}
                                        sx={{
                                            p: 2,
                                            bgcolor: isFlashing
                                                ? 'rgba(251,191,36,0.15)'
                                                : (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)'),
                                            borderRadius: 2,
                                            border: index === activeLineIndex || isFlashing ? '2px solid' : '1px solid',
                                            borderColor: isFlashing
                                                ? '#fbbf24'
                                                : (index === activeLineIndex
                                                    ? '#f9a825'
                                                    : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'divider')),
                                            boxShadow: isFlashing
                                                ? '0 0 0 4px rgba(251,191,36,0.55)'
                                                : (index === activeLineIndex
                                                    ? '0 0 0 3px rgba(249,168,37,0.25)'
                                                    : undefined),
                                            transition: 'border-color 0.2s, box-shadow 0.2s, background-color 0.2s',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Chip
                                                label={
                                                    index === activeLineIndex
                                                        ? `🎯 ${index + 1}`
                                                        : `${index + 1}`
                                                }
                                                color={index === activeLineIndex ? 'warning' : 'primary'}
                                                sx={{
                                                    minWidth: 40,
                                                    fontWeight: 'bold',
                                                    bgcolor: index === activeLineIndex
                                                        ? '#f9a825'
                                                        : (isDarkMode ? 'rgba(33,150,243,0.3)' : 'primary.main'),
                                                    color: index === activeLineIndex
                                                        ? '#000'
                                                        : (isDarkMode ? '#90caf9' : 'white'),
                                                }}
                                            />
                                            <Box sx={{ flex: 2 }}>
                                                <AccountSelector
                                                    value={line.accountId}
                                                    onChange={(value) => {
                                                        if (
                                                            value &&
                                                            isAccountUsed(
                                                                value,
                                                                line.id
                                                            )
                                                        ) {
                                                            alert(
                                                                isRTL
                                                                    ? 'هذا الحساب مستخدم بالفعل!'
                                                                    : 'This account is already used!'
                                                            );
                                                            return;
                                                        }
                                                        updateDebitLine(
                                                            line.id,
                                                            'accountId',
                                                            value
                                                        );
                                                    }}
                                                    label={
                                                        isRTL
                                                            ? 'الحساب'
                                                            : 'Account'
                                                    }
                                                    required
                                                    onlyFinalAccounts={true}
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <CostCenterSelector
                                                    value={line.costCenterId}
                                                    onChange={(value) =>
                                                        updateDebitLine(
                                                            line.id,
                                                            'costCenterId',
                                                            value
                                                        )
                                                    }
                                                    label={
                                                        isRTL
                                                            ? 'مركز التكلفة'
                                                            : 'Cost Center'
                                                    }
                                                />
                                            </Box>
                                            <TextField
                                                label={
                                                    isRTL ? 'المبلغ' : 'Amount'
                                                }
                                                type="text"
                                                inputMode="decimal"
                                                value={displayAmount(line)}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    // Accept partial valid decimals while typing (e.g. "56." or "56.5")
                                                    if (v === '' || /^\d*\.?\d{0,3}$/.test(v)) {
                                                        setAmountRaw(prev => ({ ...prev, [line.id]: v }));
                                                        const n = parseFloat(v);
                                                        updateDebitLine(line.id, 'amount', isNaN(n) ? 0 : n);
                                                    }
                                                }}
                                                onBlur={() => {
                                                    // Normalize display to 3 decimals when the user leaves the field
                                                    const n = line.amount;
                                                    setAmountRaw(prev => ({
                                                        ...prev,
                                                        [line.id]: n > 0 ? n.toFixed(3) : '',
                                                    }));
                                                }}
                                                required
                                                sx={{
                                                    width: 150,
                                                    '& .MuiInputLabel-root': {
                                                        color: isDarkMode
                                                            ? 'rgba(255,255,255,0.7)'
                                                            : 'inherit',
                                                    },
                                                    '& .MuiOutlinedInput-root':
                                                        {
                                                            color: isDarkMode
                                                                ? 'white'
                                                                : 'inherit',
                                                        },
                                                }}
                                            />
                                            <TextField
                                                label={
                                                    isRTL
                                                        ? 'الوصف'
                                                        : 'Description'
                                                }
                                                value={line.description}
                                                onChange={(e) =>
                                                    updateDebitLine(
                                                        line.id,
                                                        'description',
                                                        e.target.value
                                                    )
                                                }
                                                sx={{
                                                    flex: 1,
                                                    '& .MuiInputLabel-root': {
                                                        color: isDarkMode
                                                            ? 'rgba(255,255,255,0.7)'
                                                            : 'inherit',
                                                    },
                                                    '& .MuiOutlinedInput-root':
                                                        {
                                                            color: isDarkMode
                                                                ? 'white'
                                                                : 'inherit',
                                                        },
                                                }}
                                            />
                                            <IconButton
                                                color="error"
                                                onClick={() =>
                                                    removeDebitLine(line.id)
                                                }
                                                disabled={
                                                    debitLines.length === 1
                                                }
                                                sx={{ mt: 1 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                        {/* V2: Account alternatives — top-5 ranked suggestions from Smart Entry.
                                            Hidden when not present. Click chip to swap into this line. */}
                                        {(lineAlternatives[line.id]?.length ?? 0) > 0 && (
                                            <Box sx={{ mt: 1.5, pl: 6 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
                                                    {isRTL ? 'بدائل اقترحها المساعد:' : 'Suggested alternatives:'}
                                                </Typography>
                                                <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap' }} useFlexGap>
                                                    {(lineAlternatives[line.id] || []).map((alt) => (
                                                        <Chip
                                                            key={alt.accountId}
                                                            size="small"
                                                            label={`${alt.accountCode}  ${isRTL ? alt.accountNameAr : alt.accountName}`}
                                                            onClick={() => {
                                                                updateDebitLine(line.id, 'accountId', alt.accountId);
                                                                v2Toast(isRTL
                                                                    ? `✓ تم اختيار ${alt.accountNameAr || alt.accountName}`
                                                                    : `✓ Selected ${alt.accountName || alt.accountNameAr}`);
                                                            }}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                bgcolor: line.accountId === alt.accountId
                                                                    ? 'success.light'
                                                                    : (isDarkMode ? 'rgba(255,255,255,0.08)' : 'grey.100'),
                                                                '&:hover': {
                                                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'grey.200',
                                                                },
                                                            }}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}
                                    </Paper>
                                    );
                                })}
                            </Stack>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card
                        elevation={5}
                        sx={{
                            borderRadius: 2,
                            background: isBalanced
                                ? isDarkMode
                                    ? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
                                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                : isDarkMode
                                  ? 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)'
                                  : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                }}
                            >
                                <Typography variant="h5" fontWeight="bold">
                                    {isRTL ? '⚖️ الإجمالي' : '⚖️ Total'}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 3,
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.9 }}
                                        >
                                            {isRTL ? 'المدين' : 'Debit'}
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                        >
                                            {totalDebit.toFixed(3)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" fontWeight="bold">
                                        =
                                    </Typography>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.9 }}
                                        >
                                            {isRTL ? 'الدائن' : 'Credit'}
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                        >
                                            {totalCredit.toFixed(3)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h2" fontWeight="bold">
                                        {isBalanced ? '✅' : '❌'}
                                    </Typography>
                                </Box>
                            </Box>
                            {!isBalanced && Math.abs(difference) > 0.01 && (
                                <Alert
                                    severity="warning"
                                    sx={{
                                        mt: 2,
                                        bgcolor: isDarkMode
                                            ? 'rgba(255,255,255,0.15)'
                                            : 'rgba(255,255,255,0.9)',
                                        color: isDarkMode ? 'white' : 'inherit',
                                    }}
                                >
                                    {isRTL ? 'الفرق: ' : 'Difference: '}
                                    <strong>
                                        {Math.abs(difference).toFixed(3)}
                                    </strong>
                                    {difference > 0
                                        ? isRTL
                                            ? ' (المدين أكبر)'
                                            : ' (Debit is higher)'
                                        : isRTL
                                          ? ' (الدائن أكبر)'
                                          : ' (Credit is higher)'}
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 3,
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.100',
                    borderTop: isDarkMode
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                }}
            >
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    size="large"
                    sx={{
                        minWidth: 120,
                        color: isDarkMode ? 'white' : 'inherit',
                    }}
                >
                    {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || !isBalanced || checkingNumber}
                    size="large"
                    startIcon={loading || checkingNumber ? null : <SaveIcon />}
                    sx={{
                        minWidth: 120,
                        background: isDarkMode
                            ? 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)'
                            : 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                        boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
                    }}
                >
                    {loading || checkingNumber
                        ? isRTL
                            ? 'جارٍ المعالجة...'
                            : 'Processing...'
                        : isRTL
                          ? 'حفظ'
                          : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CashPaymentDialogV2;
